import { fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { socketService } from "./socketService";

const prepareHeaders = (headers) => {
  const token = localStorage.getItem("accessToken");
  if (token) headers.set("authorization", `Bearer ${token}`);
  return headers;
};

// Resolve base URL from environment or window.baseDomain
const resolveBaseUrl = () => {
  return (
    process.env.REACT_APP_BASE_DOMAIN ||
    window.baseDomain ||
    "https://api.happychef.cloud/"
  );
};

// Create base query with dynamic URL resolution
const httpBaseQuery = fetchBaseQuery({
  baseUrl: resolveBaseUrl(),
  credentials: "include",
  prepareHeaders,
});

/**
 * Custom base query that uses Socket.IO with HTTP fallback
 * @param {Object} args - Query arguments
 * @param {Object} api - RTK Query API
 * @param {Object} extraOptions - Extra options
 * @returns {Promise<Object>} - Query result
 */
export const socketBaseQuery = async (args, api, extraOptions) => {

  // Extract query parameters
  const { url, method = "GET", body, params } = args;

  // Determine if we should use socket or HTTP
  const useSocket = extraOptions?.useSocket !== false; // Default to true

  // Try Socket.IO first if enabled
  if (useSocket) {
    // Wait for socket to be ready (handles race condition on initial page load)
    const socketReady = await socketService.waitForReady(2000);

    if (socketReady) {
      try {
        console.log(`🔌 [socketBaseQuery] Attempting socket request: ${method} ${url}`);

        // Map HTTP method to socket event
        const eventName = mapMethodToEvent(method, url);

        // Parse query params from URL if present
        let queryParams = params || {};
        if (url.includes('?')) {
          const [, queryString] = url.split('?');
          const urlParams = new URLSearchParams(queryString);
          queryParams = {
            ...queryParams,
            ...Object.fromEntries(urlParams.entries())
          };
        }

        // Prepare socket data
        const socketData = {
          url,
          method,
          body,
          params: queryParams,
        };

        // Emit socket event
        const response = await socketService.emit(eventName, socketData);

        console.log(`✅ [socketBaseQuery] Socket request successful: ${eventName}`);

        return { data: response.data || response };
      } catch (socketError) {
        console.warn(
          `⚠️ [socketBaseQuery] Socket request failed, falling back to HTTP:`,
          socketError.message
        );

        // Fall through to HTTP fallback
      }
    } else {
      console.log(`⚠️ [socketBaseQuery] Socket not connected after wait, using HTTP`);
    }
  }

  // HTTP fallback
  console.log(`🌐 [socketBaseQuery] Using HTTP request: ${method} ${url}`);

  return httpBaseQuery(args, api, extraOptions);
};

/**
 * Map HTTP method and URL to socket event name
 * @param {string} method - HTTP method
 * @param {string} url - Request URL
 * @returns {string} - Socket event name
 */
function mapMethodToEvent(method, url) {
  // Normalize URL - remove leading slash
  const normalizedUrl = url.replace(/^\//, "");

  // Extract resource name from URL
  const resourceMatch = normalizedUrl.match(/^api\/([^/]+)/);
  const resource = resourceMatch ? resourceMatch[1] : "data";

  // Map method to action
  const methodMap = {
    GET: "fetch",
    POST: "create",
    PUT: "update",
    PATCH: "update",
    DELETE: "delete",
  };

  const action = methodMap[method.toUpperCase()] || "fetch";

  // Special handling for common patterns
  if (normalizedUrl.includes("auth-reservations")) {
    if (method === "GET") {
      if (normalizedUrl.match(/\/[a-f0-9]{24}$/i)) {
        return "reservations:fetchById";
      }
      return "reservations:fetch";
    }
    if (method === "PUT" || method === "PATCH") {
      return "reservations:updateStatus";
    }
  }

  if (normalizedUrl.includes("reservations")) {
    if (method === "GET") {
      if (normalizedUrl.match(/\/[a-f0-9]{24}$/i)) {
        return "reservations:fetchById";
      }
      return "reservations:fetch";
    }
    if (method === "POST") {
      if (normalizedUrl.includes("/auto")) {
        return "reservations:placeHold";
      }
      if (normalizedUrl.includes("/capture")) {
        return "reservations:captureHold";
      }
      if (normalizedUrl.includes("/cancel")) {
        return "reservations:cancelHold";
      }
      return "reservations:create";
    }
    if (method === "PATCH" || method === "PUT") {
      return "reservations:update";
    }
    if (method === "DELETE") {
      return "reservations:delete";
    }
  }

  // Generic event name
  return `${resource}:${action}`;
}

/**
 * Dynamic base query with socket support (backward compatible)
 */
export const dynamicBaseQuery = async (args, api, extraOptions) => {
  // If socket is available, use socket base query
  if (socketService.isConnected() && extraOptions?.useSocket !== false) {
    return socketBaseQuery(args, api, extraOptions);
  }

  // Otherwise use HTTP
  return httpBaseQuery(args, api, extraOptions);
};

export default socketBaseQuery;
